import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { NAV_ITEMS, APP_NAME, APP_SUBTITLE, LOGO_HEADER, LOGO_FOOTER, ADDRESS_LINE } from "@/lib/constants";
import { BrandLogo } from "@/components/Brand";
import { GlobalSearch } from "@/components/GlobalSearch";
import { RegistraContattoDialog } from "@/components/RegistraContattoDialog";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import * as Icons from "lucide-react";
import { Menu, Search, Bell, LogOut, UserCircle, PhoneCall } from "lucide-react";

const NavList = ({ onNavigate }) => {
  const { can, user } = useAuth();
  const isAdmin = user?.ruolo === "amministratore";
  // Alcuni moduli sono riservati al solo Amministratore (visibilità menù)
  const items = NAV_ITEMS.filter((i) => (i.adminOnly ? isAdmin : (!i.perm || can(i.perm))));
  return (
    <nav className="flex flex-col gap-0.5 px-3" data-testid="sidebar-nav">
      {items.map((item) => {
        const Icon = Icons[item.icon] || Icons.Circle;
        return (
          <NavLink key={item.to} to={item.to} end={item.end} onClick={onNavigate}
            data-testid={item.highlight ? "sidebar-centralino-link" : undefined}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                item.highlight
                  ? "bg-[color:var(--brand-orange-600)] text-white hover:bg-[color:var(--brand-orange-500)] my-1 shadow-sm"
                  : isActive
                  ? "bg-[color:var(--brand-blue-100)] text-[color:var(--brand-blue-700)]"
                  : "text-slate-600 hover:bg-slate-100"
              }`
            }>
            <Icon className="h-[18px] w-[18px] shrink-0" />
            <span className="truncate">{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
};

const SidebarContent = ({ onNavigate }) => (
  <div className="flex h-full flex-col bg-white">
    <div className="px-5 py-4 border-b">
      <BrandLogo />
    </div>
    <ScrollArea className="flex-1 py-3">
      <NavList onNavigate={onNavigate} />
    </ScrollArea>
    <div className="border-t p-4 text-[11px] text-muted-foreground">
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-[color:var(--brand-green-600)]" /> Sistema operativo
      </div>
    </div>
  </div>
);

export const ReservedLayout = () => {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [notif, setNotif] = useState({ items: [], non_lette: 0 });
  const [mobileOpen, setMobileOpen] = useState(false);

  const loadNotif = async () => {
    try { const { data } = await api.get("/notifications"); setNotif(data); } catch (e) { console.error("Errore caricamento notifiche", e); }
  };
  useEffect(() => { loadNotif(); const t = setInterval(loadNotif, 60000); return () => clearInterval(t); }, [location.pathname]);

  const markAllRead = async () => { await api.post("/notifications/read-all"); loadNotif(); };

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-[color:var(--surface-warm)] flex">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex w-[280px] shrink-0 border-r sticky top-0 h-screen" data-testid="app-sidebar">
        <SidebarContent />
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b">
          <div className="flex items-center gap-2 px-4 sm:px-6 h-16">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-[280px]"><SidebarContent onNavigate={() => setMobileOpen(false)} /></SheetContent>
            </Sheet>

            <button onClick={() => setSearchOpen(true)}
              className="flex-1 max-w-md flex items-center gap-2 rounded-lg border bg-slate-50 px-3 h-10 text-sm text-muted-foreground hover:bg-slate-100 transition-colors"
              data-testid="global-search">
              <Search className="h-4 w-4" />
              <span className="hidden sm:inline">Cerca persone, pratiche, attività…</span>
              <span className="sm:hidden">Cerca…</span>
              <kbd className="ml-auto hidden sm:inline text-[10px] border rounded px-1.5 py-0.5 bg-white">/</kbd>
            </button>

            <div className="ml-auto flex items-center gap-1.5">
              {can("interactions.write") && (
                <div className="hidden sm:block">
                  <RegistraContattoDialog onDone={() => loadNotif()} />
                </div>
              )}
              {/* Notifiche */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="notifications-button">
                    <Bell className="h-5 w-5" />
                    {notif.non_lette > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-[color:var(--brand-red-600)] text-white text-[10px] flex items-center justify-center">{notif.non_lette}</span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <div className="flex items-center justify-between px-2 py-1.5">
                    <DropdownMenuLabel className="p-0">Notifiche</DropdownMenuLabel>
                    {notif.non_lette > 0 && <button onClick={markAllRead} className="text-xs text-[color:var(--brand-blue-600)]">Segna tutte lette</button>}
                  </div>
                  <DropdownMenuSeparator />
                  <div className="max-h-80 overflow-auto ras-scroll">
                    {notif.items.length === 0 && <p className="text-sm text-muted-foreground px-3 py-6 text-center">Nessuna notifica</p>}
                    {notif.items.slice(0, 12).map((n) => (
                      <button key={n.id} onClick={() => { if (n.case_id) navigate(`/app/pratiche/${n.case_id}`); }}
                        className={`w-full text-left px-3 py-2 hover:bg-muted transition-colors ${!n.letta ? "bg-[color:var(--brand-blue-100)]/40" : ""}`}>
                        <div className="text-sm font-medium">{n.titolo}</div>
                        <div className="text-xs text-muted-foreground">{n.corpo}</div>
                      </button>
                    ))}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Profilo */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 px-2" data-testid="profile-menu-button">
                    <div className="h-8 w-8 rounded-full bg-[color:var(--brand-blue-600)] text-white flex items-center justify-center text-sm font-semibold">
                      {(user?.nome_completo || "?").split(" ").slice(0, 2).map((s) => s[0]).join("")}
                    </div>
                    <div className="hidden md:block text-left leading-tight">
                      <div className="text-sm font-medium">{user?.nome_completo}</div>
                      <div className="text-[11px] text-muted-foreground">{user?.ruolo_label || user?.ruolo}</div>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/app/profilo")}><UserCircle className="h-4 w-4 mr-2" /> Profilo personale</DropdownMenuItem>
                  <DropdownMenuItem onClick={doLogout} data-testid="logout-button"><LogOut className="h-4 w-4 mr-2" /> Esci</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 max-w-[1400px] w-full mx-auto ras-scroll">
          <Outlet />
        </main>

        <footer className="border-t bg-white" data-testid="app-footer">
          <div className="px-4 sm:px-6 lg:px-8 pt-4 pb-[calc(2rem_+_env(safe-area-inset-bottom))] sm:pb-4 flex flex-col items-center gap-2 text-center sm:flex-row sm:justify-between sm:text-left">
            <img src={LOGO_FOOTER} alt="Cantieri Educativi" className="h-10 sm:h-8 object-contain" data-testid="app-footer-logo" />
            <p className="text-[11px] text-muted-foreground sm:text-right" data-testid="app-footer-address">{ADDRESS_LINE}</p>
          </div>
        </footer>
      </div>

      <GlobalSearch open={searchOpen} setOpen={setSearchOpen} />
    </div>
  );
};
